import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { each, groupBy, map, uniqBy } from 'lodash-es';
import { AccountType } from 'src/app/auth/models/user';
import { BillingLevel, BILLINGLEVELOPTIONS } from 'src/app/utils/billingLevel';
import { creditCardOptions } from 'src/app/utils/creditCardOptions';
import { getExpiredYearOptions } from 'src/app/utils/expiredYearOptions';
import { getMonth } from 'src/app/utils/month';
import { STATES } from 'src/app/utils/states';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { FormField } from 'src/app/_models/form-field';
import { CurrencyState } from 'src/app/_models/state/optionUIState';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-billing-signup',
  templateUrl: './billing-signup.component.html',
  styleUrls: ['./billing-signup.component.scss'],
})
export class BillingSignupComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() displayCurrency: CurrencyState;
  @Output() completeForm = new EventEmitter<any>();
  isFormValid: boolean;
  billingField: FormField[];
  stateOptions: any;
  countryOptions: { display: string; value: string }[];
  private sm = new SubscriptionManager();
  accountType = AccountType;
  billingLevel = BillingLevel;
  featureCost = BILLINGLEVELOPTIONS.find(
    (F) => F.name === this.billingLevel.PREMIUM
  ).cost;
  trialPeriod = environment.trialPeriod;

  premiumBillingField: FormField[];
  trialBillingField: FormField[];
  constructor(private activateRoute: ActivatedRoute) {}

  ngOnInit(): void {
    this.stateOptions = map(groupBy(STATES, 'country'), (value, key) => {
      return { country: key, state: value };
    });

    this.countryOptions = uniqBy(STATES, 'country').map(({ country }) => ({
      display: country,
      value: country,
    }));
    this.premiumBillingField = [
      {
        fieldName: 'noOfUsers',
        labelName: 'No of Users',
        fieldType: 'number',
        value: 1,
        validations: {
          staticValidations: {
            required: true,
            min: 1,
            max: environment.maxNoOfUser,
          },
        },
        colWidth: 'col-md-4 col-sm-4',
      },
      {
        fieldName: 'totalCostPerMonth',
        labelName: 'Total Cost/Month',
        readonly: true,
        fieldType: 'readonlyText',
        value: 84.99,
        validations: {
          staticValidations: { required: true, min: 1 },
        },
        colWidth: 'offset-md-4 col-md-4 col-sm-4',
      },
      {
        fieldName: 'paymentMethod',
        labelName: 'Payment Method',
        fieldType: 'cardTitle',
        value: '',
        validations: {},
        colWidth: ' col-md-12 col-sm-12',
      },
      {
        fieldName: 'cardType',
        labelName: 'Card Type',
        fieldType: 'dropdown',
        optionLevel: 1,
        options: creditCardOptions,
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'cardHolderName',
        labelName: 'Card Holder Name',
        fieldType: 'text',

        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'cardNumber',
        labelName: 'Card Number',
        fieldType: 'mask-text',
        maskInput: '0000 0000 0000 0000 000',
        value: '',
        validations: {
          staticValidations: { creditCardValidator: true, required: true },
        },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'expirationMonth',
        labelName: 'Exp. Month',
        fieldType: 'dropdown',
        optionLevel: 1,
        options: getMonth(),
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'expirationYear',
        labelName: 'Exp. Year',
        fieldType: 'dropdown',
        optionLevel: 1,
        options: getExpiredYearOptions(),
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'cvv',
        labelName: 'CVV',
        fieldType: 'mask-text',
        maskInput: '0000',
        value: '',
        validations: { staticValidations: { checkCvv: true, required: true } },
        colWidth: ' col-md-4 col-sm-4',
      },
      {
        fieldName: 'billingAddress',
        labelName: 'Billing Address',
        fieldType: 'cardTitle',
        value: '',
        validations: {},
        colWidth: ' col-md-2 col-sm-2',
      },
      {
        fieldName: 'useAccountAddress',
        labelName: 'Use Account Address',
        fieldType: 'checkbox',
        value: '',
        validations: {},
        colWidth: ' col-md-8 col-sm-8',
      },
      {
        fieldName: 'address1',
        labelName: 'Address 1',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'address2',
        labelName: 'Address 2',
        fieldType: 'text',
        value: '',
        validations: {},
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'city',
        labelName: 'City',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'stateProvince',
        labelName: 'State/Province',
        fieldType: 'dropdown',
        options: this.stateOptions,
        optionLevel: 2,
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-3 col-sm-3',
      },
      {
        fieldName: 'zipCode',
        labelName: 'Zip Code',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-3 col-sm-3',
      },
      {
        fieldName: 'country',
        labelName: 'Country',
        fieldType: 'dropdown',
        options: this.countryOptions,
        optionLevel: 1,
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'phone',
        labelName: 'Phone',
        fieldType: 'text',
        value: '',
        validations: {
          staticValidations: { phoneNumberValidator: true, required: true },
        },
        colWidth: 'col-md-6 col-sm-6',
      },
    ];
    this.trialBillingField = [
      {
        fieldName: 'noOfUsers',
        labelName: 'No of Users',
        fieldType: 'text',
        disabled: true,
        value: 1,
        validations: {
          staticValidations: { required: true, max: 1 },
        },
        colWidth: 'col-md-4 col-sm-4',
      },
      {
        fieldName: 'totalCostPerMonth',
        labelName: 'Total Cost/Month',
        readonly: true,
        fieldType: 'readonlyText',
        value: 'FREE!',
        validations: {},
        colWidth: 'offset-md-4 col-md-4 col-sm-4',
      },

      {
        fieldName: 'billingAddress',
        labelName: 'Billing Address',
        fieldType: 'cardTitle',
        value: '',
        validations: {},
        colWidth: ' col-md-2 col-sm-2',
      },
      {
        fieldName: 'useAccountAddress',
        labelName: 'Use Account Address',
        fieldType: 'checkbox',
        value: '',
        validations: {},
        colWidth: ' col-md-8 col-sm-8',
      },
      {
        fieldName: 'address1',
        labelName: 'Address 1',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'address2',
        labelName: 'Address 2',
        fieldType: 'text',
        value: '',
        validations: {},
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'city',
        labelName: 'City',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'stateProvince',
        labelName: 'State/Province',
        fieldType: 'dropdown',
        options: this.stateOptions,
        optionLevel: 2,
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-3 col-sm-3',
      },
      {
        fieldName: 'zipCode',
        labelName: 'Zip Code',
        fieldType: 'text',
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-3 col-sm-3',
      },
      {
        fieldName: 'country',
        labelName: 'Country',
        fieldType: 'dropdown',
        options: this.countryOptions,
        optionLevel: 1,
        value: '',
        validations: { staticValidations: { required: true } },
        colWidth: 'col-md-6 col-sm-6',
      },
      {
        fieldName: 'phone',
        labelName: 'Phone',
        fieldType: 'text',
        value: '',
        validations: {
          staticValidations: { phoneNumberValidator: true, required: true },
        },
        colWidth: 'col-md-6 col-sm-6',
      },
    ];
    const queryParams = this.activateRoute.snapshot.queryParams;
    if (queryParams.featureLevel) {
      this.addDynamicJson(queryParams.featureLevel);
      this.parentForm.controls.featureLevel.setValue(queryParams?.featureLevel);
    }

    this.sm.manage =
      this.parentForm.controls.featureLevel.valueChanges.subscribe((val) => {
        this.addDynamicJson(val);
      });
  }
  addDynamicJson(val: string) {
    this.isFormValid = false;
    if (val === this.billingLevel.TRIAL) {
      this.billingField = [];
      this.billingField = [...this.trialBillingField];
    } else {
      this.billingField = [];
      this.billingField = [...this.premiumBillingField];
    }
  }
  enableContinueButton(isValid: boolean) {
    this.isFormValid = isValid;
  }
  completeBillingInformation(): void {
    this.completeForm.emit(this.isFormValid);
  }
  useAccountAddress(event: any) {
    const accountForm = this.parentForm.value.accountForm;
    const billingForm = this.parentForm.get('billingForm') as FormGroup;
    if (event) {
      billingForm.controls.address1.setValue(accountForm.address1, {
        emitEvent: false,
      });
      billingForm.controls.address2.setValue(accountForm.address2, {
        emitEvent: false,
      });
      billingForm.controls.city.setValue(accountForm.city, {
        emitEvent: false,
      });
      billingForm.controls.stateProvince.setValue(accountForm.stateProvince, {
        emitEvent: false,
      });
      billingForm.controls.country.setValue(accountForm.country, {
        emitEvent: false,
      });
      billingForm.controls.zipCode.setValue(accountForm.zipCode, {
        emitEvent: false,
      });
      billingForm.controls.phone.setValue(accountForm.phone, {
        emitEvent: false,
      });
    } else {
      billingForm.controls.address1.reset('', {
        emitEvent: false,
      });
      billingForm.controls.address2.reset('', {
        emitEvent: false,
      });
      billingForm.controls.city.reset('', {
        emitEvent: false,
      });
      billingForm.controls.stateProvince.reset('', {
        emitEvent: false,
      });
      billingForm.controls.country.reset('', {
        emitEvent: false,
      });
      billingForm.controls.zipCode.reset('', {
        emitEvent: false,
      });
      billingForm.controls.phone.reset('', {
        emitEvent: false,
      });
    }
    each(billingForm.controls, (value, res) => {
      billingForm.controls[res].markAsDirty();
    });
  }

  ngOnDestroy(): void {
    this.sm.unsubscribe();
  }
}
